import axios from 'axios';
import { makeUseAxios } from 'axios-hooks';
import React, { FC, useEffect, useState } from 'react';
import { Button } from '@clearcover/atlas';

import { LoadingPage } from 'Shared/components';
import { env } from 'Shared/constants/env';
import { makeApiHooks } from 'Shared/services/Api/makeHooks';

import { useAuth0 } from 'Auth';
import NavigationMenu from 'Core/components/NavigationMenu';
import { getRoutes } from 'Core/components/Core/getRoutes';
import { Header } from 'Core/components/Header';

import {
  lmp_quote,
  //one_inc_3_quote,
  core_quote,
} from './testInitializeQuote';
import { QuickQuoteForm } from './QuickQuoteForm';
import styles from './styles.module.css';

const axiosInstance = axios.create({
  baseURL: `${env.reznorHostname}/api/v1`,
});

const useAxios = makeUseAxios({
  axios: axiosInstance,
});

const { useLazyApi: useV1Api } = makeApiHooks(useAxios);

type StartQuoteResponseType = {
  return_url?: string;
  clearcover_application_id?: string;
  lead_metadata?: {
    campaign_medium?: string | null;
    campaign_name?: string | null;
    lead_identifier?: string | null;
    lead_name?: string | null;
    partner_unstructured_metadata?: string | null;
    attribution?: string | null;
  };
  validation_errors?: Array<any>;
  agent_return_url?: string;
};

type ButtonType = {
  label: string;
  payload: Record<string, any> | null;
  consumerApp?: boolean;
};
type Section = {
  label: string;
  buttons: ButtonType[];
};

export const Home: FC = () => {
  const { user, getToken } = useAuth0();
  const [loading, changeLoading] = useState(false);
  const [token, changeToken] = useState<string>('');
  const [consumerUrl, setConsumerUrl] = useState<string>('');

  useEffect(() => {
    const init = async () => {
      if (!token) {
        const freshToken = await getToken();
        changeToken(freshToken);
      }
    };
    init();
  }, [getToken, token]);

  const [, api] = useV1Api<StartQuoteResponseType>({
    method: 'POST',
  });

  if (!user) {
    return null;
  }

  const routes = getRoutes({ user });

  const initializeQuote = async (
    quoteData: ButtonType['payload'],
    consumerApp?: boolean
  ): Promise<void> => {
    try {
      if (quoteData?.drivers[0].contact_information) {
        quoteData.drivers[0].contact_information.email_address = `test+${Math.floor(
          10000000 * Math.random()
        )}@clearcover.com`;
      }
      const response = quoteData
        ? await api({
            url: '/quote',
            data: quoteData,
          })
        : await api({
            url: '/lead/attribution_link',
            data: {},
          });
      if (response) {
        if (consumerApp && response.data.return_url) {
          setConsumerUrl(response.data.return_url);
        } else if (response.data.agent_return_url) {
          window.location.replace(`${response.data.agent_return_url}`);
        }
      } else {
        throw new Error('Failed initializing new quote');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const initializeQuoteAndRedirect = async (
    quoteData: ButtonType['payload'],
    consumerApp?: boolean
  ) => {
    changeLoading(true);
    setConsumerUrl('');
    try {
      await initializeQuote(quoteData, consumerApp);
    } catch (err) {
      console.error(err);
    } finally {
      changeLoading(false);
    }
  };

  const sections: Section[] = [
    {
      label: 'AQUI Quick Links',
      buttons: [
        {
          label: 'LMP',
          payload: lmp_quote,
        },
        // {
        //   label: 'OneInc 3.0',
        //   payload: one_inc_3_quote,
        // },
        {
          label: 'Core',
          payload: core_quote,
        },
        {
          label: 'Blank Quote',
          payload: null,
        },
      ],
    },
    {
      label: 'Consumer App Quick Links',
      buttons: [
        {
          label: 'LMP',
          payload: lmp_quote,
          consumerApp: true,
        },
        // {
        //   label: 'OneInc 3.0',
        //   payload: one_inc_3_quote,
        //   consumerApp: true,
        // },
        {
          label: 'Core',
          payload: core_quote,
          consumerApp: true,
        },
        {
          label: 'Blank Quote',
          payload: null,
          consumerApp: true,
        },
      ],
    },
  ];

  return (
    <>
      <NavigationMenu
        navItems={routes.map(({ component, ...navProps }) => ({
          ...navProps,
        }))}
      />
      <div className={styles.wrapper}>
        <Header />
        {loading ? (
          <LoadingPage />
        ) : (
          <>
            <h1>Agent Quoting Demo Page</h1>
            <QuickQuoteForm
              onSubmit={(payload) => {
                initializeQuoteAndRedirect(payload);
              }}
            />
            {sections.map((section, i) => {
              return (
                <div className={styles.section} key={`section-${i}`}>
                  <h2>{section.label}</h2>
                  <div className={styles.buttonWrapper}>
                    {section.buttons.map((button, j) => {
                      return (
                        <Button
                          key={`button-${i}-${j}`}
                          className={styles.button}
                          variant="primary"
                          onClick={() =>
                            initializeQuoteAndRedirect(
                              button.payload,
                              button.consumerApp
                            )
                          }
                          disabled={loading}
                        >
                          {button.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {consumerUrl && (
              <div className={styles.section}>
                <div className={styles.consumerUrl}>
                  <p>
                    Tip: Right click and open this link in an incognito window
                  </p>
                  <a href={consumerUrl}>{consumerUrl}</a>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
